import React from 'react';
import { Box, Typography, Chip, Button, FormControl, InputLabel, Select, MenuItem, Divider } from '@mui/material';
import { SchoolPicker } from './primitives';
import { adminColors, TOUCH_HIT_SX } from '../../theme/adminTheme';
import { ROLES, ROLE_LABELS, isSchoolAdmin, isMasterAdmin } from '../../constants/roles';
import { staffTierGuard, roleEditGuard } from './adminGuards';

// Role / School / Access editing block — identical between StudentDrawer
// and StaffDrawer (the only real difference between the two callers is
// *which roles* `pickableRoles` offers, already computed by each via
// assignableRoles(currentUserRole)). Extracted so the exact per-field
// guard rules (staffTierGuard/roleEditGuard, see adminGuards.js) can never
// drift between a student's Account section and a staff member's.
//
// Only ever rendered by a caller that has already gated it on
// isAdminRole(currentUserRole) — this component doesn't re-check that
// itself, same as the two blocks it replaces didn't.
export default function AccountSection({
  user,
  currentUserRole,
  currentUser,
  pickableRoles,
  onRoleSelect,
  onSchoolSelect,
  onAccessClick,
}) {
  return (
    <>
      <Divider sx={{ my: 3, borderColor: 'rgba(24,24,27,0.08)' }} />
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: adminColors.ink, mb: 1.5 }}>
        Account
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <FormControl size="small" fullWidth disabled={roleEditGuard(currentUserRole, user.role)}>
          <InputLabel id="drawer-role-label">Role</InputLabel>
          <Select
            labelId="drawer-role-label"
            label="Role"
            value={user.role || ROLES.STUDENT}
            onChange={(e) => onRoleSelect(e.target.value)}
          >
            <MenuItem value={ROLES.STUDENT}>Student</MenuItem>
            {pickableRoles.includes(ROLES.TEACHER) && <MenuItem value={ROLES.TEACHER}>Teacher</MenuItem>}
            {pickableRoles.includes(ROLES.SCHOOL_ADMIN) && <MenuItem value={ROLES.SCHOOL_ADMIN}>School Admin</MenuItem>}
            {pickableRoles.includes(ROLES.MASTER_ADMIN) && <MenuItem value={ROLES.MASTER_ADMIN}>Master Admin</MenuItem>}
            {/* Legacy lowercase "admin" (pre-dates the MASTER_ADMIN/
                SCHOOL_ADMIN split, see constants/roles.js) is never in
                pickableRoles/assignableRoles — nobody may newly assign it.
                But a user can still legitimately *have* it, and with no
                matching MenuItem the Select rendered blank for that row.
                Shown here as the row's own current value, disabled so it
                can't be (re)selected — same "unassignable but must still
                render" case Select options generally handle via `disabled`
                rather than omission. */}
            {user.role === ROLES.LEGACY_ADMIN && !pickableRoles.includes(ROLES.LEGACY_ADMIN) && (
              <MenuItem value={ROLES.LEGACY_ADMIN} disabled>{ROLE_LABELS[ROLES.LEGACY_ADMIN]}</MenuItem>
            )}
          </Select>
        </FormControl>

        <SchoolPicker
          value={user.school || ''}
          onChange={onSchoolSelect}
          currentUserRole={currentUserRole}
          lockedSchoolName={user.school}
          label="School"
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="body2" sx={{ color: adminColors.inkSoft, minWidth: 56 }}>
            Access
          </Typography>
          <Chip
            label={user.access_granted !== false ? 'Granted' : 'Revoked'}
            color={user.access_granted !== false ? 'success' : 'error'}
            size="small"
            variant="outlined"
          />
          <Button
            size="small"
            variant="text"
            color={user.access_granted !== false ? 'error' : 'success'}
            onClick={onAccessClick}
            disabled={
              // Same three-part guard StudentDrawer/UsersView's row-level
              // Access button always used: a SCHOOL_ADMIN can't touch a
              // MASTER_ADMIN/another SCHOOL_ADMIN's access or their own,
              // and a MASTER_ADMIN can't revoke their own access (granting
              // it back to themselves is still allowed).
              staffTierGuard(currentUserRole, user.role) ||
              (isSchoolAdmin(currentUserRole) && user.id === currentUser?.id) ||
              (isMasterAdmin(user.role) && user.id === currentUser?.id && user.access_granted !== false)
            }
            sx={TOUCH_HIT_SX}
          >
            {user.access_granted !== false ? 'Revoke' : 'Grant'}
          </Button>
        </Box>
      </Box>
    </>
  );
}
